import { BellIcon, GearIcon } from '../launcher/icons';
import { tint } from '../launcher/format';
import { LogoMark } from '../lib/LogoMark';
import { Avatar } from './Avatar';
import s from './HeroVisual.module.css';

const PEOPLE = [
  { name: 'Alex', at: s.atLeft },
  { name: 'Maya', at: s.atRight },
  { name: 'Mom', at: s.atBottom },
];

const TILES = [
  { slug: 'hello', icon: '👋', name: 'Hello' },
  { slug: 'chores', icon: '🧹', name: 'Chores' },
  { slug: 'shopping', icon: '🛒', name: 'Shopping', arriving: true },
];

/**
 * The hero's product picture: the logo's ring of people, drawn large, around a phone that shows
 * the launcher as Anna sees it, and the builder's prompt box beside it. One 12 s CSS loop tells
 * the mechanic: the prompt is typed and built, "Alex sent Shopping" arrives, the tile appears.
 * The resting styles are the loop's last beat, which is all reduced motion shows.
 */
export function HeroVisual() {
  return (
    <div
      className={s.visual}
      role="img"
      aria-label="Alex asks the builder for a shared shopping list. Anna's phone gets “Alex sent Shopping” and the Shopping app appears in her launcher next to Hello and Chores, inside a circle with Alex, Maya and Mom."
    >
      <div className={s.stage}>
        <div className={s.ring} />
        {PEOPLE.map((p) => (
          <span key={p.name} className={`${s.person} ${p.at}`}>
            <Avatar name={p.name} className={s.avatar} />
            <span className={s.personName}>{p.name}</span>
          </span>
        ))}

        <div className={s.phone}>
          <div className={s.screen}>
            <div className={s.status}>
              <span>9:41</span>
              <span className={s.island} />
              <span className={s.statusIcons}>
                <svg width="16" height="10" viewBox="0 0 16 10" aria-hidden>
                  <rect x="0" y="6" width="3" height="4" rx="1" />
                  <rect x="4.3" y="4" width="3" height="6" rx="1" />
                  <rect x="8.6" y="2" width="3" height="8" rx="1" />
                  <rect x="12.9" y="0" width="3" height="10" rx="1" />
                </svg>
                <svg width="22" height="11" viewBox="0 0 22 11" aria-hidden>
                  <rect x="0.5" y="0.5" width="18" height="10" rx="3" fill="none" stroke="currentColor" opacity="0.45" />
                  <rect x="2" y="2" width="13" height="7" rx="1.6" />
                  <rect x="19.6" y="3.5" width="1.6" height="4" rx="0.8" opacity="0.45" />
                </svg>
              </span>
            </div>

            <div className={s.banner}>
              <LogoMark size={26} className={s.bannerIcon} />
              <span className={s.bannerText}>
                <span className={s.bannerHead}>
                  <span>Bolt Circles</span>
                  <span>now</span>
                </span>
                <strong>Alex sent Shopping</strong>
                <span>Tap to open it.</span>
              </span>
            </div>

            <div className={s.topBar}>
              <LogoMark size={24} />
              <span className={s.topIcons}>
                <BellIcon size={17} />
                <GearIcon size={17} />
              </span>
            </div>

            <div className={s.hello}>
              <span className={s.hiRow}>
                <Avatar name="Anna" className={s.me} />
                <span className={s.hi}>Hi, Anna</span>
              </span>
              <span className={s.people}>
                <span className={s.faces}>
                  {PEOPLE.map((p) => (
                    <Avatar key={p.name} name={p.name} className={s.face} />
                  ))}
                </span>
                Family with Alex, Maya and Mom
              </span>
            </div>

            <div className={s.tiles}>
              {TILES.map((t) => (
                <span key={t.slug} className={`${s.tile} ${t.arriving ? s.arriving : ''}`}>
                  <span className={s.tileIcon} style={{ background: tint(t.slug) }}>
                    {t.icon}
                  </span>
                  <span className={s.tileName}>{t.name}</span>
                </span>
              ))}
            </div>

            <span className={s.homeBar} />
          </div>
        </div>

        <div className={s.composer}>
          <span className={s.prompt}>
            <span className={s.placeholder}>Describe the app…</span>
            <span className={s.line1}>A shared shopping list</span>
            <span className={s.line2}>for the four of us</span>
          </span>
          <span className={s.composerFoot}>
            <span className={s.target}>
              <LogoMark size={14} />
              Family
            </span>
            <span className={s.build}>Build</span>
          </span>
        </div>
      </div>
    </div>
  );
}
